/**
 * Phase 1 — Reusable duplicate-part warning dialog.
 *
 * Used by every part-creation entry point in the app:
 *   - Catalogue / Parts List
 *   - Inventory page "add part" flow
 *   - PO line-item inline quick-add
 *   - Job material-request flow
 *   - Vehicle inventory add-part
 *
 * Caller wires three handlers:
 *   onUseExisting(item)  — pick this existing item INSTEAD of creating new
 *                          (in inline flows: select+attach to the row;
 *                           on standalone Catalogue: open the matching row's
 *                           edit drawer).
 *   onCreateAnyway()     — proceed with the create the user originally wanted.
 *   onCancel()           — abort; return user to the create form.
 *
 * Props:
 *   open               boolean
 *   duplicates         response.data.duplicates from itemsAPI.checkDuplicates
 *   onUseExisting, onCreateAnyway, onCancel
 *   context            "catalogue" | "inline" — tweaks the language on the
 *                      "Use Existing Part" button. Defaults to "catalogue".
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle } from 'lucide-react';

const CONFIDENCE_STYLES = {
  high:   'bg-rose-100 text-rose-800 border-rose-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low:    'bg-slate-100 text-slate-700 border-slate-200',
};

export function DuplicatePartWarning({
  open,
  duplicates = [],
  onUseExisting,
  onCreateAnyway,
  onCancel,
  context = 'catalogue',
}) {
  if (!duplicates || duplicates.length === 0) return null;

  const useExistingLabel = context === 'inline' ? 'Use This Part' : 'Open Existing Part';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) onCancel?.(); }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-part-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Possible duplicate found
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600 dark:text-slate-300">
            We found <strong>{duplicates.length}</strong> existing {duplicates.length === 1 ? 'part' : 'parts'} that look like what you're creating. Pick one if it's the same thing — or create yours anyway.
          </p>
          <div className="space-y-2">
            {duplicates.map((d) => (
              <div
                key={d.item_id}
                className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                data-testid={`duplicate-candidate-${d.item_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{d.name}</span>
                      <Badge variant="outline" className={CONFIDENCE_STYLES[d.confidence] || ''} data-testid={`duplicate-confidence-${d.item_id}`}>
                        {d.confidence}
                      </Badge>
                      {d.is_archived && (
                        <Badge variant="outline" className="bg-slate-100 text-slate-500">archived</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{d.reason}</p>
                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                      {d.sku && <span><span className="text-slate-400">SKU:</span> {d.sku}</span>}
                      {d.manufacturer_part_number && <span><span className="text-slate-400">P/N:</span> {d.manufacturer_part_number}</span>}
                      {d.category_names?.length > 0 && <span className="col-span-2"><span className="text-slate-400">Categories:</span> {d.category_names.join(', ')}</span>}
                      <span><span className="text-slate-400">Warehouse:</span> {d.warehouse_qty}</span>
                      <span><span className="text-slate-400">Vehicles:</span> {d.vehicle_qty}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onUseExisting?.(d)}
                    data-testid={`duplicate-use-existing-${d.item_id}`}
                    className="shrink-0"
                  >
                    {useExistingLabel}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onCancel?.()} data-testid="duplicate-cancel-btn">Cancel</Button>
          <Button onClick={() => onCreateAnyway?.()} data-testid="duplicate-create-anyway-btn">Create Anyway</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Convenience hook: runs the dup check, exposes a `requireConfirm(payload, onConfirm)`
 * function the caller invokes from its onSubmit. Handles the dialog state internally.
 *
 * Usage:
 *   const dup = useDuplicateCheck({ context: 'inline' });
 *   ...
 *   <DuplicatePartWarning {...dup.dialogProps} />
 *   ...
 *   const submit = async () => {
 *     await dup.guardedCreate(
 *       { name, sku, manufacturer_part_number, category_ids, exclude_item_id },
 *       () => actuallyCreateTheItem(),
 *       (item) => attachExistingItem(item),
 *     );
 *   };
 */
export function useDuplicateCheck({ context = 'catalogue' } = {}) {
  const [open, setOpen] = React.useState(false);
  const [duplicates, setDuplicates] = React.useState([]);
  const pending = React.useRef(null);  // { onCreate, onUseExisting }

  const guardedCreate = React.useCallback(async (payload, onCreate, onUseExisting) => {
    const { itemsAPI } = await import('../lib/api');
    let res;
    try {
      res = await itemsAPI.checkDuplicates(payload);
    } catch (e) {
      // If the check itself fails, don't block the user — proceed with create.
      await onCreate?.();
      return;
    }
    const candidates = res?.data?.duplicates || [];
    if (candidates.length === 0) {
      await onCreate?.();
      return;
    }
    pending.current = { onCreate, onUseExisting };
    setDuplicates(candidates);
    setOpen(true);
  }, []);

  const handleUseExisting = React.useCallback(async (item) => {
    const { onUseExisting } = pending.current || {};
    setOpen(false);
    setDuplicates([]);
    pending.current = null;
    await onUseExisting?.(item);
  }, []);
  const handleCreateAnyway = React.useCallback(async () => {
    const { onCreate } = pending.current || {};
    setOpen(false);
    setDuplicates([]);
    pending.current = null;
    await onCreate?.();
  }, []);
  const handleCancel = React.useCallback(() => {
    setOpen(false);
    setDuplicates([]);
    pending.current = null;
  }, []);

  return {
    guardedCreate,
    dialogProps: {
      open,
      duplicates,
      context,
      onUseExisting: handleUseExisting,
      onCreateAnyway: handleCreateAnyway,
      onCancel: handleCancel,
    },
  };
}
